import {
    DateTimeLike,
    formatDateAsDayMonthYearMinimalString,
    mostRecentValidMarketDateAsSortableString
} from "@/helpers/dateTimeUtilities";

const AerospaceAirlines: number = 140;

const AutosParts: number = 141;
const Banks: number = 142;
const BiomedicsGenetics: number = 143;
const Buildings: number = 144;
const BusinessPrdSvcs: number = 145;
const Chemicals: number = 146;
const Computers: number = 147;
const Drugs: number = 148;
const Electronics: number = 149;
const EnergyOther: number = 150;
const Finance: number = 151;
const FoodsBevgsSoaps: number = 152;
const ForestPrdsPaper: number = 153;
const Gaming: number = 154;
const Healthcare: number = 155;
const HouseholdGoods: number = 156;
const Insurance: number = 157;
const InternationalAsiaPac: number = 158;
const InternationalChina: number = 159;
const InternationalEurope: number = 160;
// const InternationalJapan: number = 161;
const InternationalLatinAmerica: number = 162;
const Internet: number = 163;
const Leisure: number = 164;
const MachineryTools: number = 165;
const Media: number = 166;
const MetalsNonFerrous: number = 167;
const OilCoal: number = 168;
const OilService: number = 169;
const PollutionControl: number = 170;
const PreciousMetals: number = 171;
const RealEstate: number = 173;
const Restaurants: number = 174;
const Retailing: number = 175;
const SavingsLoans: number = 176;
const Semiconductors: number = 177;
const Software: number = 178;
const SteelIron: number = 179;
const Telecom: number = 180;
const TextilesApparel: number = 181;
const TransportNonAir: number = 182;
const UtilityElectric: number = 183;
const UtilityGas: number = 184;
const WallStreet: number = 185;
const ProtectionSftyEqp: number = 186;
const ETFsFunds: number = 187;

export interface IChartUriBuilder {
    buildPercentTenChartUriBySectorId( sectorId: number, date: DateTimeLike ): string;
    buildPercentThirtyChartUriBySectorId( sectorId: number, date: DateTimeLike ): string;
    buildBullishPercentChartUriBySectorId( sectorId: number, date?: DateTimeLike ): string;
    buildEquallyWeightedSectorChartUriBySectorId( iiChartId: string, date: DateTimeLike ): string;
    buildRelativeStrengthChartUriBySectorId( sectorId: number, date: DateTimeLike ): string;
}

const WidthReplacementKey: string = '[WIDTH]';
const HeightReplacementKey: string = '[HEIGHT]';
const ChartIdReplacementKey: string = '[CHART_ID]';
const DateReplacementKey: string = '[DD_MM_YY]';

const stdWidth: number = 600;
const stdHeight: number = 400;
const ExtraLargeWidth: string = `${stdWidth + 370}`;
const ExtraLargeHeight: string = `${stdHeight + 180}`;

const AerospaceAirlinesChartId = 134667;
const AutosPartsChartId = 134699;
const BanksChartId = 134731;
const BiomedicsGeneticsChartId = 134739;
const BuildingsChartId = 134775;
const BusinessPrdSvcsChartId = 134823;
const ChemicalsChartId = 134851;

const ComputersChartId = 134875;
const DrugsChartId = 134891;
const ElectronicsChartId = 134943;
const EnergyOtherChartId = 134955;
const ETFsFundsChartId = 134915;
const FinanceChartId = 134975;
const FoodsBevgsSoapsChartId = 135023;
const ForestPrdsPaperChartId = 135035;
const GamingChartId = 135043;
const HealthcareChartId = 135087;
const HouseholdGoodsChartId = 135107;
const InsuranceChartId = 135131;
const InternationalAsiaPacChartId = 135139;
const InternationalChinaChartId = 135147;
const InternationalEuropeChartId = 135155;
const InternationalLatinAmericaChartId = 135163;

const InternetChartId = 135175;
const LeisureChartId = 135203;
const MachineryToolsChartId = 135231;
const MediaChartId = 135259;
const MetalsNonFerrousChartId = 135271;
const OilCoalChartId = 135299;
const OilServiceChartId = 135319;
const PollutionControlChartId = 135323;

const PreciousMetalsChartId = 135331;
const ProtectionSftyEqpChartId = 135339;
const RealEstateChartId = 135355;
const RestaurantsChartId = 135363;
const RetailingChartId = 135415;
const SavingsLoansChartId = 135423;
const SemiconductorsChartId = 135431;

const SoftwareChartId = 135475;
const SteelIronChartId = 135487;
const TelecomChartId = 135523;
const TextilesApparelChartId = 135535;
const TransportNonAirChartId = 135559;
const UtilityElectricChartId = 135567;
const UtilityGasChartId = 135583;
const WallStreetChartId = 135595;

class InvestorIntelligenceChartUriBuilder implements IChartUriBuilder {
    private readonly EquallyWeightedSectorPnFChartUriTemplate: string = `https://pr.ecube.co.uk/tmi/pfchart?detail=n&type=pnf&_period=2y&_cdata=C&_ctrend=Y&_cbox=2&_creverse=3&w=${WidthReplacementKey}&h=${HeightReplacementKey}&id=${ChartIdReplacementKey}&d=${DateReplacementKey}`;
    private readonly SectorPercentTenWeekPnFChartUriTemplate: string = `https://pr.ecube.co.uk/tmi/pfchart?detail=y&type=pnf&_period=2y&_cdata=C&_cbox=3&_creverse=3&_cscale=d&w=${WidthReplacementKey}&h=${HeightReplacementKey}&id=${ChartIdReplacementKey}&d=${DateReplacementKey}`;
    private readonly SectorPercentThirtyWeekPnFChartUriTemplate: string = `https://pr.ecube.co.uk/tmi/pfchart?detail=y&type=pnf&_period=2y&_cdata=C&_cbox=3&_creverse=3&_cscale=d&w=${WidthReplacementKey}&h=${HeightReplacementKey}&id=${ChartIdReplacementKey}&d=${DateReplacementKey}`;
    private readonly SectorRelativeStrengthPnFChartUriTemplate: string = `https://pr.ecube.co.uk/tmi/pfchart?detail=y&type=pnf&_period=2y&_cdata=C&_cbox=3&_creverse=3&_cscale=d&w=${WidthReplacementKey}&h=${HeightReplacementKey}&id=${ChartIdReplacementKey}&d=${DateReplacementKey}`;
    private readonly SectorPercentBullishPercentPnFChartUriTemplate: string = `https://pr.ecube.co.uk/tmi/bp-pfchart?w=${WidthReplacementKey}&h=${HeightReplacementKey}&id=${ChartIdReplacementKey}&d=${DateReplacementKey}`;

    private readonly _sectorIdToPercentTenWeekChartIdMap: Map< number, string > = new Map< number, string >();
    private readonly _sectorIdToPercentThirtyWeekChartIdMap: Map< number, string > = new Map< number, string >();
    private readonly _sectorIdToRelativeStrengthChartIdMap: Map< number, string > = new Map< number, string >();

    // The mapping comes from the data based on the following query: SELECT sector_id, chart_id FROM sector_alerts GROUP BY chart_id, sector_id;
    private readonly _sectorIdToBpiChartIdMap: Map< number, string > = new Map< number, string >();

    private initializeChartIdsByTmiId( tmiId: number, baseSectorChartId: number ) {
        this._sectorIdToBpiChartIdMap.set( tmiId, `${ baseSectorChartId }` );
        this._sectorIdToRelativeStrengthChartIdMap.set( tmiId, `${ baseSectorChartId + 1 }` );
        this._sectorIdToPercentTenWeekChartIdMap.set( tmiId, `${ baseSectorChartId + 2 }` );
        this._sectorIdToPercentThirtyWeekChartIdMap.set( tmiId, `${ baseSectorChartId + 3 }` );
    }

    constructor() {
        this.initializeChartIdsByTmiId( AerospaceAirlines, AerospaceAirlinesChartId );
        this.initializeChartIdsByTmiId( AutosParts, AutosPartsChartId );
        this.initializeChartIdsByTmiId( Banks, BanksChartId );
        this.initializeChartIdsByTmiId( BiomedicsGenetics, BiomedicsGeneticsChartId );
        this.initializeChartIdsByTmiId( Buildings, BuildingsChartId );
        this.initializeChartIdsByTmiId( BusinessPrdSvcs, BusinessPrdSvcsChartId );
        this.initializeChartIdsByTmiId( Chemicals, ChemicalsChartId );
        this.initializeChartIdsByTmiId( Computers, ComputersChartId );
        this.initializeChartIdsByTmiId( Drugs, DrugsChartId );
        this.initializeChartIdsByTmiId( Electronics, ElectronicsChartId );
        this.initializeChartIdsByTmiId( EnergyOther, EnergyOtherChartId );
        this.initializeChartIdsByTmiId( Finance, FinanceChartId );
        this.initializeChartIdsByTmiId( FoodsBevgsSoaps, FoodsBevgsSoapsChartId );
        this.initializeChartIdsByTmiId( ForestPrdsPaper, ForestPrdsPaperChartId );
        this.initializeChartIdsByTmiId( Gaming, GamingChartId );
        this.initializeChartIdsByTmiId( Healthcare, HealthcareChartId );
        this.initializeChartIdsByTmiId( HouseholdGoods, HouseholdGoodsChartId );
        this.initializeChartIdsByTmiId( Insurance, InsuranceChartId );
        this.initializeChartIdsByTmiId( InternationalAsiaPac, InternationalAsiaPacChartId );
        this.initializeChartIdsByTmiId( InternationalChina, InternationalChinaChartId );
        this.initializeChartIdsByTmiId( InternationalEurope, InternationalEuropeChartId );
        this.initializeChartIdsByTmiId( InternationalLatinAmerica, InternationalLatinAmericaChartId );
        this.initializeChartIdsByTmiId( Internet, InternetChartId );
        this.initializeChartIdsByTmiId( Leisure, LeisureChartId );
        this.initializeChartIdsByTmiId( MachineryTools, MachineryToolsChartId );
        this.initializeChartIdsByTmiId( Media, MediaChartId );
        this.initializeChartIdsByTmiId( MetalsNonFerrous, MetalsNonFerrousChartId );
        this.initializeChartIdsByTmiId( OilCoal, OilCoalChartId );
        this.initializeChartIdsByTmiId( OilService, OilServiceChartId );
        this.initializeChartIdsByTmiId( PollutionControl, PollutionControlChartId );
        this.initializeChartIdsByTmiId( PreciousMetals, PreciousMetalsChartId );
        this.initializeChartIdsByTmiId( RealEstate, RealEstateChartId );
        this.initializeChartIdsByTmiId( Restaurants, RestaurantsChartId );
        this.initializeChartIdsByTmiId( Retailing, RetailingChartId );
        this.initializeChartIdsByTmiId( SavingsLoans, SavingsLoansChartId );
        this.initializeChartIdsByTmiId( Semiconductors, SemiconductorsChartId );
        this.initializeChartIdsByTmiId( Software, SoftwareChartId );
        this.initializeChartIdsByTmiId( SteelIron, SteelIronChartId );
        this.initializeChartIdsByTmiId( Telecom, TelecomChartId );
        this.initializeChartIdsByTmiId( TextilesApparel, TextilesApparelChartId );
        this.initializeChartIdsByTmiId( TransportNonAir, TransportNonAirChartId );
        this.initializeChartIdsByTmiId( UtilityElectric, UtilityElectricChartId );
        this.initializeChartIdsByTmiId( UtilityGas, UtilityGasChartId );
        this.initializeChartIdsByTmiId( WallStreet, WallStreetChartId );
        this.initializeChartIdsByTmiId( ProtectionSftyEqp, ProtectionSftyEqpChartId );
        this.initializeChartIdsByTmiId( ETFsFunds, ETFsFundsChartId );
    }

    public buildPercentTenChartUriBySectorId( sectorId: number, date: DateTimeLike ): string {
        const dateAsString: string = formatDateAsDayMonthYearMinimalString( date );
        const chartId: string = this._sectorIdToPercentTenWeekChartIdMap.get( sectorId )!;
        return this.SectorPercentTenWeekPnFChartUriTemplate
                        .replace( WidthReplacementKey, ExtraLargeWidth )
                        .replace( HeightReplacementKey, ExtraLargeHeight )
                        .replace( ChartIdReplacementKey, chartId )
                        .replace( DateReplacementKey, dateAsString );
                }

    public buildPercentThirtyChartUriBySectorId( sectorId: number, date: DateTimeLike ): string {
        const dateAsString: string = formatDateAsDayMonthYearMinimalString( date );
        const percentThirtyChartId: string = this._sectorIdToPercentThirtyWeekChartIdMap.get( sectorId )!;
        return this.SectorPercentThirtyWeekPnFChartUriTemplate
                   .replace( WidthReplacementKey, ExtraLargeWidth )
                   .replace( HeightReplacementKey, ExtraLargeHeight )
                   .replace( ChartIdReplacementKey, percentThirtyChartId )
                   .replace( DateReplacementKey, dateAsString );
    }

    public buildBullishPercentChartUriBySectorId( sectorId: number, date?: DateTimeLike ): string {
        const chartId: string = this._sectorIdToBpiChartIdMap.get( sectorId )!;

        if ( null == date ) {
            date = mostRecentValidMarketDateAsSortableString( date );
        }

        const dateAsString: string = formatDateAsDayMonthYearMinimalString( date );
        return this.SectorPercentBullishPercentPnFChartUriTemplate
                   .replace( WidthReplacementKey, ExtraLargeWidth )
                   .replace( HeightReplacementKey, ExtraLargeHeight )
                   .replace( ChartIdReplacementKey, chartId )
                   .replace( DateReplacementKey, dateAsString );
    }

    public buildEquallyWeightedSectorChartUriBySectorId( iiChartId: string, date: DateTimeLike ): string {
        const dateAsString: string = formatDateAsDayMonthYearMinimalString( date );
        return this.EquallyWeightedSectorPnFChartUriTemplate
            .replace( WidthReplacementKey, ExtraLargeWidth )
            .replace( HeightReplacementKey, ExtraLargeHeight )
            .replace( ChartIdReplacementKey, iiChartId )
            .replace( DateReplacementKey, dateAsString );
    }

    public buildRelativeStrengthChartUriBySectorId( sectorId: number, date: DateTimeLike ): string {
        const dateAsString: string = formatDateAsDayMonthYearMinimalString( date );
        const rsChartId: string = this._sectorIdToRelativeStrengthChartIdMap.get( sectorId )!;
        return this.SectorRelativeStrengthPnFChartUriTemplate
            .replace( WidthReplacementKey, ExtraLargeWidth )
            .replace( HeightReplacementKey, ExtraLargeHeight )
            .replace( ChartIdReplacementKey, rsChartId )
            .replace( DateReplacementKey, dateAsString );
    }
}

const iiChartUriBuilder: IChartUriBuilder = new InvestorIntelligenceChartUriBuilder();
export { iiChartUriBuilder };
